import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Team, CreateTeamDto, UpdateTeamDto, ApiResponse, PaginatedResponse, TeamCompetencyAnalysisDTO } from '../../../shared/types';
import axiosService from '../services/axios.service';
import { useCurrentCrewMember } from './useCurrentCrewMember';

// Team search parameters
export interface TeamSearchParams {
    page?: number;
    limit?: number;
    query?: string;
    status?: 'active' | 'inactive' | 'disbanded' | 'all';
    competencies?: string[];
    memberId?: string;
}

// Query key factory
export const teamKeys = {
    all: ['teams'] as const,
    crews: (crewSlug: string) => [...teamKeys.all, 'crew', crewSlug] as const,
    list: (crewSlug: string, page?: number, limit?: number) =>
    [...teamKeys.crews(crewSlug), 'list', { page, limit }] as const,
    search: (crewSlug: string, params: TeamSearchParams) =>
    [...teamKeys.crews(crewSlug), 'search', params] as const,
    detail: (crewSlug: string, teamSlug: string) =>
    [...teamKeys.crews(crewSlug), 'detail', teamSlug] as const,
    subTeams: (crewSlug: string, teamSlug: string) =>
    [...teamKeys.detail(crewSlug, teamSlug), 'sub-teams'] as const,
    membersInTree: (crewSlug: string, teamSlug: string) =>
    [...teamKeys.detail(crewSlug, teamSlug), 'members-in-tree'] as const
};

// API functions
const teamApi = {
    getTeams: async (crewSlug: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<Team>> => {
        const { data } = await axiosService.get<PaginatedResponse<Team>>(`/c/${crewSlug}/teams`, {
            params: { page, limit }
        });
        return data;
    },

    getTeam: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.get<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}`);
        return data;
    },

    getTeamsByMember: async (crewSlug: string, memberId: string): Promise<ApiResponse<Team[]>> => {
        const { data } = await axiosService.get<ApiResponse<Team[]>>(`/c/${crewSlug}/teams/member/${memberId}`);
        return data;
    },

    createTeam: async (crewSlug: string, teamData: CreateTeamDto): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.post<ApiResponse<Team>>(`/c/${crewSlug}/teams`, teamData);
        return data;
    },

    updateTeam: async (crewSlug: string, teamSlug: string, teamData: UpdateTeamDto): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.put<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}`, teamData);
        return data;
    },

    addTeamMember: async (crewSlug: string, teamSlug: string, memberId: string): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.post<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}/members`, {
            memberId
        });
        return data;
    },

    removeTeamMember: async (crewSlug: string, teamSlug: string, memberId: string): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.delete<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}/members/${memberId}`);
        return data;
    },

    addTeamAdmin: async (crewSlug: string, teamSlug: string, adminId: string): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.post<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}/admins`, {
            adminId
        });
        return data;
    },

    removeTeamAdmin: async (crewSlug: string, teamSlug: string, adminId: string): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.delete<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}/admins/${adminId}`);
        return data;
    },

    joinTeam: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<null>> => {
        const { data } = await axiosService.post<ApiResponse<null>>(`/c/${crewSlug}/teams/${teamSlug}/join`);
        return data;
    },

    searchTeams: async (crewSlug: string, params: TeamSearchParams): Promise<PaginatedResponse<Team>> => {
        const { page = 1, limit = 20, query, status, competencies, memberId } = params;
        const { data } = await axiosService.get<PaginatedResponse<Team>>(`/c/${crewSlug}/teams`, {
            params: {
                page,
                limit,
                q: query || undefined,
                status: status || undefined,
                competencies: competencies?.length ? competencies.join(',') : undefined,
                memberId: memberId || undefined
            }
        });
        return data;
    },

    seedTeams: async (crewSlug: string): Promise<ApiResponse<{ created: Team[], skipped: string[] }>> => {
        const { data } = await axiosService.post<ApiResponse<{ created: Team[], skipped: string[] }>>(`/c/${crewSlug}/teams/seed`);
        return data;
    },

    updateTeamStatus: async (crewSlug: string, teamSlug: string, status: 'active' | 'inactive' | 'disbanded'): Promise<ApiResponse<Team>> => {
        const { data } = await axiosService.patch<ApiResponse<Team>>(`/c/${crewSlug}/teams/${teamSlug}/status`, {
            status
        });
        return data;
    },

    deleteTeam: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<{ deleted: boolean }>> => {
        const { data } = await axiosService.delete<ApiResponse<{ deleted: boolean }>>(`/c/${crewSlug}/teams/${teamSlug}`);
        return data;
    },

    getTeamAuditLog: async (crewSlug: string, teamSlug: string, limit: number = 50, offset: number = 0): Promise<ApiResponse<{ logs: any[], total: number }>> => {
        const { data } = await axiosService.get<ApiResponse<{ logs: any[], total: number }>>(`/c/${crewSlug}/teams/${teamSlug}/audit-log`, {
            params: { limit, offset }
        });
        return data;
    },

    generateCompetencyAnalysis: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<TeamCompetencyAnalysisDTO>> => {
        const { data } = await axiosService.post<ApiResponse<TeamCompetencyAnalysisDTO>>(`/c/${crewSlug}/teams/${teamSlug}/competency-analysis`);
        return data;
    },

    getLatestCompetencyAnalysis: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<TeamCompetencyAnalysisDTO>> => {
        const { data } = await axiosService.get<ApiResponse<TeamCompetencyAnalysisDTO>>(`/c/${crewSlug}/teams/${teamSlug}/competency-analysis/latest`);
        return data;
    },

    updateAnalysisVisibility: async (
        crewSlug: string,
        teamSlug: string,
        analysisId: string,
        isVisibleToMembers: boolean
    ): Promise<ApiResponse<TeamCompetencyAnalysisDTO>> => {
        const { data } = await axiosService.patch<ApiResponse<TeamCompetencyAnalysisDTO>>(
            `/c/${crewSlug}/teams/${teamSlug}/competency-analysis/${analysisId}/visibility`,
            { isVisibleToMembers }
        );
        return data;
    },

    getSubTeams: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<Team[]>> => {
        const { data } = await axiosService.get<ApiResponse<Team[]>>(`/c/${crewSlug}/teams/${teamSlug}/sub-teams`);
        return data;
    },

    getMembersInTree: async (crewSlug: string, teamSlug: string): Promise<ApiResponse<string[]>> => {
        const { data } = await axiosService.get<ApiResponse<string[]>>(`/c/${crewSlug}/teams/${teamSlug}/members-in-tree`);
        return data;
    },

    getAnalysisHistory: async (
        crewSlug: string,
        teamSlug: string,
        page: number = 1,
        limit: number = 10
    ): Promise<PaginatedResponse<TeamCompetencyAnalysisDTO>> => {
        const { data } = await axiosService.get<PaginatedResponse<TeamCompetencyAnalysisDTO>>(
            `/c/${crewSlug}/teams/${teamSlug}/competency-analysis/history`,
            { params: { page, limit } }
        );
        return data;
    }
};

// React Query hooks
export const useTeams = (crewSlug: string, page: number = 1, limit: number = 10) => {
    return useQuery({
        queryKey: teamKeys.list(crewSlug, page, limit),
        queryFn: () => teamApi.getTeams(crewSlug, page, limit),
        enabled: !!crewSlug
    });
};

export const useTeam = (crewSlug: string, teamSlug: string) => {
    return useQuery({
        queryKey: teamKeys.detail(crewSlug, teamSlug),
        queryFn: () => teamApi.getTeam(crewSlug, teamSlug),
        select: (res) => res.data,
        enabled: !!crewSlug && !!teamSlug
    });
};

export const useTeamsByMember = (crewSlug: string, memberIdOverride?: string) => {
    const { data: currentMember } = useCurrentCrewMember(crewSlug);
    const memberId = memberIdOverride ?? currentMember?._id;
    return useQuery({
        queryKey: [...teamKeys.crews(crewSlug), 'member', memberId],
        queryFn: async () => {
            const response = await teamApi.getTeamsByMember(crewSlug, memberId!);
            return response.data ?? [];
        },
        enabled: !!crewSlug && !!memberId
    });
};

export const useCreateTeam = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (teamData: CreateTeamDto) => teamApi.createTeam(crewSlug, teamData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

export const useUpdateTeam = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ teamSlug, data }: { teamSlug: string; data: UpdateTeamDto }) =>
            teamApi.updateTeam(crewSlug, teamSlug, data),
        onSuccess: (_, { teamSlug }) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

export const useAddTeamMember = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ teamSlug, memberId }: { teamSlug: string; memberId: string }) =>
            teamApi.addTeamMember(crewSlug, teamSlug, memberId),
        onSuccess: (_, { teamSlug }) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

export const useJoinTeam = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (teamSlug: string) => teamApi.joinTeam(crewSlug, teamSlug),
        onSuccess: (_, teamSlug) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

export const useRemoveTeamMember = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ teamSlug, memberId }: { teamSlug: string; memberId: string }) =>
            teamApi.removeTeamMember(crewSlug, teamSlug, memberId),
        onSuccess: (_, { teamSlug }) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

export const useAddTeamAdmin = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ teamSlug, adminId }: { teamSlug: string; adminId: string }) =>
            teamApi.addTeamAdmin(crewSlug, teamSlug, adminId),
        onSuccess: (_, { teamSlug }) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

export const useRemoveTeamAdmin = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ teamSlug, adminId }: { teamSlug: string; adminId: string }) =>
            teamApi.removeTeamAdmin(crewSlug, teamSlug, adminId),
        onSuccess: (_, { teamSlug }) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

/**
 * Search teams with filtering support
 * Supports text search, status filter, competency filter, and member filter
 */
export const useTeamSearch = (crewSlug: string, params: TeamSearchParams, enabled: boolean = true) => {
    return useQuery({
        queryKey: teamKeys.search(crewSlug, params),
        queryFn: () => teamApi.searchTeams(crewSlug, params),
        enabled: !!crewSlug && enabled,
        staleTime: 30000, // 30 seconds
        placeholderData: (previousData) => previousData
    });
};

/**
 * Seed teams from org structure
 * Creates a team for each manager with their direct reports as members
 * Skips teams that already exist (by name)
 */
export const useSeedTeams = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => teamApi.seedTeams(crewSlug),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

/**
 * Update team status (active/inactive/disbanded)
 * Only crew admins and team admins can update status
 */
export const useUpdateTeamStatus = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ teamSlug, status }: { teamSlug: string; status: 'active' | 'inactive' | 'disbanded' }) =>
            teamApi.updateTeamStatus(crewSlug, teamSlug, status),
        onSuccess: (_, { teamSlug }) => {
            queryClient.invalidateQueries({ queryKey: teamKeys.detail(crewSlug, teamSlug) });
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

/**
 * Delete team
 * Only crew admins and team admins can delete teams
 */
export const useDeleteTeam = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (teamSlug: string) =>
            teamApi.deleteTeam(crewSlug, teamSlug),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.crews(crewSlug) });
        }
    });
};

/**
 * Get team audit log
 * Returns activity history for the team
 */
export const useTeamAuditLog = (crewSlug: string, teamSlug: string, limit: number = 50) => {
    return useQuery({
        queryKey: [...teamKeys.detail(crewSlug, teamSlug), 'audit-log', limit],
        queryFn: () => teamApi.getTeamAuditLog(crewSlug, teamSlug, limit),
        enabled: !!crewSlug && !!teamSlug,
        staleTime: 30000 // 30 seconds
    });
};

/**
 * Generate team competency analysis
 * Creates key person risk analysis for team competencies
 */
export const useGenerateCompetencyAnalysis = (crewSlug: string, teamSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => teamApi.generateCompetencyAnalysis(crewSlug, teamSlug),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...teamKeys.detail(crewSlug, teamSlug), 'analysis'] });
        }
    });
};

/**
 * Get latest competency analysis
 * Returns most recent key person risk analysis for the team
 */
export const useLatestCompetencyAnalysis = (crewSlug: string, teamSlug: string) => {
    return useQuery({
        queryKey: [...teamKeys.detail(crewSlug, teamSlug), 'analysis', 'latest'],
        queryFn: () => teamApi.getLatestCompetencyAnalysis(crewSlug, teamSlug),
        enabled: !!crewSlug && !!teamSlug,
        retry: false // Don't retry if analysis doesn't exist yet
    });
};

/**
 * Update analysis visibility
 * Toggles whether team members can view the analysis
 */
export const useUpdateAnalysisVisibility = (crewSlug: string, teamSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ analysisId, isVisibleToMembers }: { analysisId: string; isVisibleToMembers: boolean }) =>
            teamApi.updateAnalysisVisibility(crewSlug, teamSlug, analysisId, isVisibleToMembers),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...teamKeys.detail(crewSlug, teamSlug), 'analysis'] });
        }
    });
};

/**
 * Get analysis history
 * Returns paginated list of historical analyses
 */
export const useAnalysisHistory = (crewSlug: string, teamSlug: string, page: number = 1, limit: number = 10) => {
    return useQuery({
        queryKey: [...teamKeys.detail(crewSlug, teamSlug), 'analysis', 'history', { page, limit }],
        queryFn: () => teamApi.getAnalysisHistory(crewSlug, teamSlug, page, limit),
        enabled: !!crewSlug && !!teamSlug
    });
};

/**
 * Get direct sub-teams (children) of a team
 */
export const useSubTeams = (crewSlug: string, teamSlug: string) => {
    return useQuery({
        queryKey: teamKeys.subTeams(crewSlug, teamSlug),
        queryFn: () => teamApi.getSubTeams(crewSlug, teamSlug),
        enabled: !!crewSlug && !!teamSlug
    });
};

/**
 * Get all member IDs in a team's subtree (direct + all descendants)
 * Returns string[] of profile IDs
 */
export const useMembersInTree = (crewSlug: string, teamSlug: string) => {
    return useQuery({
        queryKey: teamKeys.membersInTree(crewSlug, teamSlug),
        queryFn: () => teamApi.getMembersInTree(crewSlug, teamSlug),
        enabled: !!crewSlug && !!teamSlug
    });
};
